import { Badge, Button, Group, Menu, Modal, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconChevronDown,
  IconClock,
  IconEdit,
  IconMailForward,
  IconRefresh,
  IconUpload,
  IconUserCheck,
  IconUsers,
  IconUserUp,
  IconUserX,
} from '@tabler/icons-react'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import type { PaginationState, SortingState, Updater } from '@tanstack/react-table'
import { functionalUpdate } from '@tanstack/react-table'
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'

import { Table, TableRowActionButton } from '@/components/Table'
import { TruncatedText } from '@/components/TruncatedText'
import { useBillingDisabledStatus } from '@/hooks/useBillingDisabledStatus'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { UserRole, UserSortBy } from '@/services/api/api.types'
import { type GetUsersResponseEntry } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { formatDate } from '@/utils/dates'
import { useNavigateTo } from '@/utils/navigation'
import type { TableCellContext, TableColumnDef } from '@/utils/table'
import { createTableColumnHelper } from '@/utils/table'

import type { AllowedUserRole } from './AddUserModal'
import { AddUserModal } from './AddUserModal'
import { BulkUploadModal } from './BulkUploadModal'
import { EditUserModal } from './EditUserModal'
import { RoleTag } from './RoleTag'
import { formatTrialDuration, getTrialState, RESTART_TRIAL_ENABLED, TRIAL_DAYS, TRIAL_DURATION_LABEL } from './trial'

// Table Context
// --------------------------------------------------------------------------------
type TableContext = {
  changeUserStatus: (data: GetUsersResponseEntry, isDisabled: boolean) => void
  confirmRemoveUser: (user: GetUsersResponseEntry) => void
  editUser: (user: GetUsersResponseEntry) => void
  reinviteUser: (user: GetUsersResponseEntry) => void
  restartTrial: (user: GetUsersResponseEntry) => void
  requestConvertToFullUser: (user: GetUsersResponseEntry) => void
}

const TableContext = createContext<TableContext>({} as TableContext)
const useTableContext = () => useContext(TableContext)

const columnHelper = createTableColumnHelper<GetUsersResponseEntry>()

const idToSortBy = (id: string): UserSortBy | undefined => {
  switch (id) {
    case 'name':
      return 'name'
    case 'email':
      return 'email'
    // TODO: Temporarily disabled - will be restored later
    // case 'lastLogin':
    //   return 'last_login'
    case 'createdAt':
      return 'created_at'
    case 'isUserDisabled':
      return 'is_user_disabled'
    case 'totalCreditsUsed':
      return 'total_credits_used'
    case 'lastActivityUpdatedAt':
      return 'last_activity_updated_at'
    case 'status':
      return 'status'
    default:
      return undefined
  }
}

interface UserManagementProps {
  entityName: string
  role?: UserRole
  showRoleColumn?: boolean
  canInvite?: boolean
}

export function UserManagement({ entityName, role, showRoleColumn = true, canInvite = true }: UserManagementProps) {
  const { api, uiStore } = useGlobalState()
  const navigateTo = useNavigateTo()

  const [sorting, setSorting] = useState<SortingState>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filter, setFilter] = useState('')

  // Determine if role should be passed to modals (only for allowed roles)
  const modalRoleProps = role && ['SME', 'User', 'Admin'].includes(role) ? { role: role as AllowedUserRole } : {}

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  const [bulkModalOpened, { open: openBulkModal, close: closeBulkModal }] = useDisclosure(false)
  const [bulkIsTrial, setBulkIsTrial] = useState(false)
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false)
  const [paymentRequiredModalOpened, { open: openPaymentRequiredModal, close: closePaymentRequiredModal }] =
    useDisclosure(false)
  const [convertTrialModalOpened, { open: openConvertTrialModal, close: closeConvertTrialModal }] = useDisclosure(false)
  const [selectedUser, setSelectedUser] = useState<GetUsersResponseEntry | null>(null)
  const [userToConvert, setUserToConvert] = useState<GetUsersResponseEntry | null>(null)
  const [paymentBlockReason, setPaymentBlockReason] = useState<'billing' | 'freeSeats' | null>(null)

  // Check billing status for invite restrictions
  const { isDisabledDueToBilling, ignoreBilling } = useBillingDisabledStatus()
  const { data: billingData } = useQuery({
    queryKey: ['billing', 'info'],
    queryFn: () => api.getBillingInfo(),
  })
  const billingInfo = billingData?.billingInfo

  const { data, isLoading } = useQuery({
    queryKey: ['users', 'list', page, pageSize, sorting, filter, role],
    queryFn: () => {
      const sortBy = idToSortBy(sorting[0]?.id)
      const sortDir = sorting[0]?.desc == undefined ? undefined : sorting[0]?.desc ? 'desc' : 'asc'
      return api.getUsers({ page, pageSize, sortBy, sortDir, filterBy: filter, role, isUserDisabled: false })
    },
    placeholderData: keepPreviousData,
  })

  const changeUserStatusMutation = useMutation({
    mutationFn: api.changeUserStatus,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
    },
  })

  function changeUserStatus(data: GetUsersResponseEntry, isDisabled: boolean) {
    changeUserStatusMutation.mutate({
      user_id: data.userId,
      email: data.email,
      is_user_disabled: isDisabled,
    })
  }

  function confirmRemoveUser(user: GetUsersResponseEntry) {
    const identifier = user.name?.trim() || user.email?.trim() || ''
    const message = identifier
      ? `Are you sure you want to remove ${identifier}? They will no longer be able to access your channel and will not count for billed users after this cycle. You cannot undo this action, but you can invite them again.`
      : 'Are you sure you want to remove this user?'

    modals.openConfirmModal({
      title: 'Remove User?',
      centered: true,
      children: <Text size="sm">{message}</Text>,
      labels: { confirm: 'Remove User', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () => changeUserStatus(user, true),
    })
  }

  const reinviteUserMutation = useMutation({
    mutationFn: api.reinviteUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      notifications.show({
        title: 'Invite Sent',
        message: 'A new invitation has been sent to the user.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  function reinviteUser(user: GetUsersResponseEntry) {
    reinviteUserMutation.mutate({
      user_details: {
        user_id: user.userId,
        email: user.email,
        sme: user.smeAccess,
        sponsor_admin: user.isSponsorAdmin,
        name: user.name ?? undefined,
      },
    })
  }

  function editUser(user: GetUsersResponseEntry) {
    setSelectedUser(user)
    openEditModal()
  }

  function buildBaseUpdateParams(user: GetUsersResponseEntry) {
    return {
      email: user.email,
      user_id: user.userId,
      name: user.name ?? undefined,
      sme: user.smeAccess,
      sponsor_admin: user.isSponsorAdmin,
      is_user_disabled: user.status === 'disabled',
    }
  }

  const restartTrialMutation = useMutation({
    mutationFn: api.updateUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: (response, variables) => {
      const success = 'success' in response && response.success
      if (success) {
        api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
        notifications.show({
          title: 'Success',
          message: `Trial restarted (${formatTrialDuration(variables.trial_days)}).`,
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to restart trial. Please try again.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
  })

  const convertTrialMutation = useMutation({
    mutationFn: api.updateUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: response => {
      const success = 'success' in response && response.success
      if (success) {
        api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
        notifications.show({
          title: 'Success',
          message: 'Account upgraded.',
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to upgrade account. Please try again.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
  })

  function restartTrial(user: GetUsersResponseEntry) {
    restartTrialMutation.mutate({ ...buildBaseUpdateParams(user), trial_days: user.trialDays ?? TRIAL_DAYS })
  }

  function requestConvertToFullUser(user: GetUsersResponseEntry) {
    setUserToConvert(user)
    openConvertTrialModal()
  }

  function confirmConvertToFullUser() {
    if (!userToConvert) return
    convertTrialMutation.mutate({ ...buildBaseUpdateParams(userToConvert), is_trial: false })
    closeConvertTrialModal()
    setUserToConvert(null)
  }

  // Table Functionality
  // ------------------------------------------------------------
  const onPaginationChange = (updater: Updater<PaginationState>) => {
    const next = functionalUpdate(updater, { pageIndex: page - 1, pageSize })
    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  const columns = useMemo(() => getColumns(showRoleColumn, role), [showRoleColumn, role])

  const handleClosePaymentModal = () => {
    closePaymentRequiredModal()
    setPaymentBlockReason(null)
  }

  // Both single-invite and bulk-upload create billable users, so they share the
  // same payment-method gate before the relevant modal can open.
  const guardBillingThen = (open: () => void) => {
    if (ignoreBilling) return open()
    if (isDisabledDueToBilling) {
      setPaymentBlockReason('billing')
      return openPaymentRequiredModal()
    }
    if (!billingInfo?.paymentMethod && (billingInfo?.freeSeats ?? 0) <= 0) {
      setPaymentBlockReason('freeSeats')
      return openPaymentRequiredModal()
    }
    open()
  }

  const openBulkUpload = (isTrial: boolean) => {
    guardBillingThen(() => {
      setBulkIsTrial(isTrial)
      openBulkModal()
    })
  }

  return (
    <TableContext.Provider
      value={{ changeUserStatus, confirmRemoveUser, editUser, reinviteUser, restartTrial, requestConvertToFullUser }}>
      <Paper radius="md" shadow="sm">
        <Table
          columns={columns}
          data={data?.members ?? noMembers}
          enableSorting
          sorting={sorting}
          onSortingChange={setSorting}
          manualFiltering
          onGlobalFilterChange={setFilter}
          pagination={{
            state: { pageIndex: page - 1, pageSize },
            rowCount: data?.total ?? 0,
            onChange: onPaginationChange,
            showRowsPerPage: false,
          }}
          isLoading={isLoading}
          paperProps={{
            px: 'xs',
            py: 'xl',
          }}
          entity={{ singular: entityName, plural: entityName + 's', Icon: IconUsers }}
          toolbarActionsInclude={['new']}
          toolbarActionsLabelMap={{ new: 'Invite' }}
          toolbarActionsDisabled={{ new: !canInvite }}
          extraToolbarActions={
            role === 'User' ? (
              <Menu position="bottom-end" withArrow>
                <Menu.Target>
                  <Button
                    variant="outline"
                    leftSection={<IconUpload size={16} />}
                    rightSection={<IconChevronDown size={16} />}
                    disabled={!canInvite}>
                    Bulk Upload Users
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconUpload size={16} />} onClick={() => openBulkUpload(false)}>
                    <Text size="sm">Bulk Upload Users</Text>
                    <Text size="xs" c="dimmed">
                      Add users with full access
                    </Text>
                  </Menu.Item>
                  <Menu.Item leftSection={<IconClock size={16} />} onClick={() => openBulkUpload(true)}>
                    <Text size="sm">Bulk Invite Trial Users</Text>
                    <Text size="xs" c="dimmed">
                      Add users on a {TRIAL_DURATION_LABEL} trial
                    </Text>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : undefined
          }
          onToolbarActionClick={action => {
            if (action === 'new') {
              guardBillingThen(openModal)
            }
          }}
        />
      </Paper>

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        centered
        size="lg"
        styles={{
          body: {
            paddingTop: '0rem',
          },
          header: {
            paddingBottom: '0rem',
          },
        }}>
        <AddUserModal entityName={entityName} closeModal={closeModal} {...modalRoleProps} />
      </Modal>

      <BulkUploadModal opened={bulkModalOpened} isTrial={bulkIsTrial} onClose={closeBulkModal} />

      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        centered
        size="lg"
        styles={{
          body: {
            paddingTop: '0rem',
          },
          header: {
            paddingBottom: '0rem',
          },
        }}>
        {selectedUser && (
          <EditUserModal entityName={entityName} closeModal={closeEditModal} user={selectedUser} {...modalRoleProps} />
        )}
      </Modal>

      <Modal
        opened={convertTrialModalOpened}
        onClose={() => {
          closeConvertTrialModal()
          setUserToConvert(null)
        }}
        centered
        size="md"
        title="Upgrade Account">
        <Stack gap="md">
          <Text size="sm">
            This will remove the trial limit on{' '}
            <Text component="span" fw={600}>
              {userToConvert?.name || userToConvert?.email}
            </Text>
            . They will have full AI access from now on. This cannot be undone from this screen.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                closeConvertTrialModal()
                setUserToConvert(null)
              }}>
              Cancel
            </Button>
            <Button onClick={confirmConvertToFullUser} loading={convertTrialMutation.isPending}>
              Upgrade Account
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={paymentRequiredModalOpened}
        onClose={handleClosePaymentModal}
        centered
        size="md"
        withCloseButton={false}>
        <Stack gap="lg" py="md" align="center">
          {paymentBlockReason === 'billing' && <IconAlertTriangle size={48} color="var(--mantine-color-orange-6)" />}

          <Stack gap="xs" align="center">
            {paymentBlockReason === 'billing' && (
              <Text size="lg" fw={600} ta="center">
                Payment Method Required
              </Text>
            )}
            <Text size="sm" c="dimmed" ta="center">
              To invite additional End Users or Experts, you must add a valid payment method for your channel.
            </Text>
          </Stack>

          <Group justify="center" gap="md" mt="md">
            <Button variant="default" onClick={handleClosePaymentModal}>
              {paymentBlockReason === 'freeSeats' ? 'Stay in Free Trial' : 'Cancel'}
            </Button>
            <Button
              onClick={() => {
                handleClosePaymentModal()
                navigateTo('billing', { searchParams: { tab: 'payment' } })
              }}>
              Add Payment Method
            </Button>
          </Group>
        </Stack>
      </Modal>
    </TableContext.Provider>
  )
}

const noMembers: GetUsersResponseEntry[] = []

const getColumns = (showRoleColumn: boolean, role?: UserRole): TableColumnDef<GetUsersResponseEntry>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    size: 150,
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return <TruncatedText style={{ maxWidth: '100%' }}>{value || ''}</TruncatedText>
    },
  },
  ...(showRoleColumn
    ? [
        {
          header: 'Role',
          meta: { grow: false },
          enableSorting: false,
          accessorFn: (row: GetUsersResponseEntry) => {
            if (row.isSwiAdmin) return 'SWI Admin'
            if (row.isSponsorAdmin) return 'Admin'
            if (row.smeAccess) return 'SME'
            return 'User'
          },
          cell: ({ cell }: TableCellContext<GetUsersResponseEntry>) => (
            <div style={{ paddingLeft: 0 }}>
              <RoleTag role={cell.getValue() as UserRole} />
            </div>
          ),
        },
      ]
    : []),
  {
    header: 'Email',
    accessorKey: 'email',
    minSize: 200,
    sortDescFirst: false,
    cell: ({ cell }) => {
      const value = cell.getValue<string>()
      return <TruncatedText style={{ maxWidth: '100%' }}>{value}</TruncatedText>
    },
  },
  {
    accessorKey: 'totalCreditsUsed',
    size: 130,
    meta: { grow: false, label: 'Messages' },
    enableSorting: true,
    sortDescFirst: true,
    header: () => (
      <Tooltip label="Number of messages sent" withArrow>
        <span style={{ cursor: 'help' }}>Messages</span>
      </Tooltip>
    ),
    cell: ({ cell }) => {
      const value = cell.getValue<number>()
      return <div style={{ textAlign: 'center', width: '100%' }}>{value?.toLocaleString() ?? '0'}</div>
    },
  },
  // TODO: Temporarily hidden - will be restored later
  // {
  //   header: 'Last Login',
  //   accessorKey: 'lastLogin',
  //   enableSorting: true,
  //   sortDescFirst: false,
  //   grow: false,
  //   size: 130,
  //   Cell: ({ cell }) => {
  //     const value = cell.getValue<string | null>()
  //     return value ? (
  //       formatDate(value, { format: 'MMM D, YYYY' }) || ''
  //     ) : (
  //       <Text c="gray.6" fs="italic">
  //         Invite Sent
  //       </Text>
  //     )
  //   },
  // },
  {
    header: 'Date Added',
    accessorKey: 'createdAt',
    enableSorting: true,
    sortDescFirst: false,
    size: 135,
    meta: { grow: false },
    cell: ({ cell }) => {
      const value = cell.getValue<string | null>()
      return value ? (
        formatDate(value, { format: 'MMM D, YYYY' }) || ''
      ) : (
        <Text c="gray.6" fs="italic">
          Unknown
        </Text>
      )
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    size: 130,
    meta: { grow: false, align: 'center' },
    enableSorting: true,
    sortDescFirst: false,
    cell: ({ row }) => {
      const status = row.original.status
      const trialState = getTrialState(row.original)

      let statusText: ReactNode
      switch (status) {
        case 'disabled':
          statusText = <Text c="red">Removed</Text>
          break
        case 'expired':
          statusText = <Text c="gray.6">Invitation Expired</Text>
          break
        case 'trial_expired':
          statusText = <Text c="red">Trial Expired</Text>
          break
        case 'subscribed':
          statusText = (
            <Text c="green.6" fw={500}>
              Subscribed
            </Text>
          )
          break
        case 'invited':
          statusText = <Text c="green.6">Invited</Text>
          break
        case 'active':
        default:
          statusText = (
            <Text c="green.6" fw={500}>
              Active
            </Text>
          )
      }

      // Trial badge rides alongside the lifecycle status so the SME can spot trial
      // accounts at any stage (including before the user has logged in). Suppressed
      // when the account is removed (trial state is moot) or when status itself is
      // 'trial_expired' -- the main status message already communicates that.
      let trialBadge: ReactNode = null
      if (status !== 'disabled' && status !== 'trial_expired' && trialState.kind !== 'none') {
        if (trialState.kind === 'active') {
          trialBadge = (
            <Tooltip
              label={`Trial ends ${formatDate(trialState.endsAt, { format: 'MMM D, YYYY' })} (${trialState.daysRemaining} day${trialState.daysRemaining === 1 ? '' : 's'} remaining) - ${formatTrialDuration(row.original.trialDays)} trial`}
              withArrow>
              <Badge color="indigo" variant="light" size="xs" style={{ cursor: 'help' }}>
                Trial Active
              </Badge>
            </Tooltip>
          )
        } else if (trialState.kind === 'expired') {
          trialBadge = (
            <Tooltip label={`Trial expired on ${formatDate(trialState.endedAt, { format: 'MMM D, YYYY' })}`} withArrow>
              <Badge color="red" variant="light" size="xs" style={{ cursor: 'help' }}>
                Trial Expired
              </Badge>
            </Tooltip>
          )
        } else {
          trialBadge = (
            <Tooltip label={`Trial starts on first login (${formatTrialDuration(row.original.trialDays)})`} withArrow>
              <Badge color="indigo" variant="light" size="xs" style={{ cursor: 'help' }}>
                Trial Pending
              </Badge>
            </Tooltip>
          )
        }
      }

      if (!trialBadge) return statusText
      return (
        <Stack gap={4} align="center">
          {statusText}
          {trialBadge}
        </Stack>
      )
    },
  },
  ...(role === 'User'
    ? [
        {
          header: 'Last Active',
          accessorKey: 'lastActivityUpdatedAt',
          enableSorting: true,
          sortDescFirst: true,
          size: 130,
          meta: { grow: false },
          cell: ({ cell }: TableCellContext<GetUsersResponseEntry>) => {
            const value = cell.getValue<string | null>()
            return value ? (
              formatDate(value, { format: 'MMM D, YYYY' }) || ''
            ) : (
              <Text c="gray.6" fs="italic">
                No Activity
              </Text>
            )
          },
        },
      ]
    : []),
  columnHelper.display({
    id: 'actions',
    header: '',
    size: 50,
    meta: { grow: false },
    cell: ({ row }) => <UserActionsCell row={row} />,
  }),
]

function UserActionsCell({ row }: Pick<TableCellContext<GetUsersResponseEntry>, 'row'>) {
  const { changeUserStatus, confirmRemoveUser, editUser, reinviteUser, restartTrial, requestConvertToFullUser } =
    useTableContext()
  const { authenticationStore } = useGlobalState()
  const status = row.original.status
  const showResendInvite = status === 'expired' || status === 'invited'
  const isCurrentUser = authenticationStore.user?.userId === row.original.userId
  const isEndUser = !row.original.smeAccess && !row.original.isSponsorAdmin && !row.original.isSwiAdmin
  const isOnAnyTrial = isEndUser && row.original.isTrial && status !== 'disabled'
  const trialState = getTrialState(row.original)
  const showRestartTrial = RESTART_TRIAL_ENABLED && isOnAnyTrial && trialState.kind === 'expired'
  const showUpgradeAccount = isOnAnyTrial

  return (
    <Group gap="xs" justify="flex-end" wrap="nowrap">
      <Menu withArrow position="bottom-end">
        <Menu.Target>
          <TableRowActionButton action="menu" tooltipLabel="More actions" />
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Actions:</Menu.Label>
          <Menu.Item onClick={() => editUser(row.original)} leftSection={<IconEdit />}>
            Edit
          </Menu.Item>
          {showResendInvite && (
            <Menu.Item onClick={() => reinviteUser(row.original)} leftSection={<IconMailForward />}>
              Resend Invite
            </Menu.Item>
          )}
          {showRestartTrial && (
            <Menu.Item onClick={() => restartTrial(row.original)} leftSection={<IconRefresh />}>
              Restart Trial ({formatTrialDuration(row.original.trialDays)})
            </Menu.Item>
          )}
          {showUpgradeAccount && (
            <Menu.Item onClick={() => requestConvertToFullUser(row.original)} leftSection={<IconUserUp />}>
              Upgrade Account
            </Menu.Item>
          )}
          {status === 'disabled' ? (
            <Menu.Item
              color="green"
              onClick={() => changeUserStatus(row.original, false)}
              leftSection={<IconUserCheck />}>
              Activate
            </Menu.Item>
          ) : isCurrentUser ? (
            <Tooltip label="You cannot remove yourself from this channel" withArrow zIndex={301}>
              <div>
                <Menu.Item color="red" disabled leftSection={<IconUserX />} closeMenuOnClick={false}>
                  Remove
                </Menu.Item>
              </div>
            </Tooltip>
          ) : (
            <Menu.Item color="red" onClick={() => confirmRemoveUser(row.original)} leftSection={<IconUserX />}>
              Remove
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}
